import React, { useState } from 'react';
import { ArrowLeft, Users, Coins, ExternalLink, CheckCircle, Clock, Building, Calendar } from 'lucide-react';
import { ActionPanel } from './ActionPanel';
import { ActionDetailPanel } from './ActionDetailPanel';
import { RosterModal } from './RosterModal';
import { EmptyState } from './EmptyState';
import { useToast } from './Toast';
interface Site {
  id: string;
  name: string;
  projectedCost: string;
  affectedGuards: number;
  overtimeHours: string;
  hasAction: boolean;
}
interface OvertimeCostSiteListProps {
  provinceName: string;
  provinceId: string;
  areaName: string;
  areaId: string;
  onBack: () => void;
}
// Mock data for sites
const sitesByArea: Record<string, Site[]> = {
  'ct-cbd': [{
    id: 'woolworths-hq',
    name: 'Woolworths HQ',
    projectedCost: 'R45,000',
    affectedGuards: 3,
    overtimeHours: '82 hrs',
    hasAction: false
  }, {
    id: 'std-bank',
    name: 'Standard Bank Tower',
    projectedCost: 'R28,500',
    affectedGuards: 2,
    overtimeHours: '52 hrs',
    hasAction: true
  }],
  // Default fallback for other areas
  default: [{
    id: 'site-1',
    name: 'Generic Office Park',
    projectedCost: 'R12,500',
    affectedGuards: 1,
    overtimeHours: '24 hrs',
    hasAction: false
  }]
};
export function OvertimeCostSiteList({
  provinceName,
  areaName,
  areaId,
  onBack
}: OvertimeCostSiteListProps) {
  const {
    showToast
  } = useToast();
  const sites = sitesByArea[areaId] || sitesByArea.default;
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [viewingActionSite, setViewingActionSite] = useState<Site | null>(null);
  const [rosterSite, setRosterSite] = useState<Site | null>(null);
  const handleTakeAction = (site: Site) => {
    setSelectedSite(site);
  };
  const handleViewAction = (site: Site) => {
    setViewingActionSite(site);
  };
  const handleViewRoster = (site: Site) => {
    setRosterSite(site);
  };
  const handleClosePanel = () => {
    setSelectedSite(null);
  };
  const handleCloseDetailPanel = () => {
    setViewingActionSite(null);
  };
  const handleCloseRoster = () => {
    setRosterSite(null);
  };
  const handleSubmitAction = (data: any) => {
    console.log('Action created:', data);
    showToast('Action created successfully', 'success');
    handleClosePanel();
  };
  return <>
      <div className="flex flex-col space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-872">
        {/* Header Section */}
        <div className="flex flex-col space-y-2" data-id="element-873">
          <button onClick={onBack} className="group flex w-fit items-center text-sm font-medium text-[#5a7280] hover:text-[#062133] transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-[#062133]/5" aria-label="Back to area list" data-id="element-874">
            <ArrowLeft className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-1" data-id="element-875" />
            Back to {provinceName}
          </button>

          <div className="flex items-center justify-between" data-id="element-876">
            <div data-id="element-877">
              <h2 className="text-2xl font-bold text-[#062133]" data-id="element-878">
                Overtime Details in {areaName}
              </h2>
              <p className="text-[#5a7280]" data-id="element-879">
                Review sites with projected overtime and take action
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Table View (hidden on mobile) */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm" data-id="element-880">
          <table className="min-w-full divide-y divide-[#e5e5e5]" data-id="element-881">
            <thead className="bg-[#fafaf9]" data-id="element-882">
              <tr data-id="element-883">
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-884">
                  Site Name
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-885">
                  Projected Overtime
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-886">
                  Affected Guards
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-887">
                  Overtime Hours
                </th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-888">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e5e5e5]" data-id="element-889">
              {sites.length > 0 ? sites.map(site => <tr key={site.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => handleViewRoster(site)} data-id="element-890">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-891">
                      <div className="text-sm font-semibold text-[#062133]" data-id="element-892">
                        {site.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-893">
                      <div className="flex items-center text-sm font-medium text-rose-600" data-id="element-894">
                        <Coins className="mr-1.5 h-4 w-4" data-id="element-895" />
                        {site.projectedCost}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-896">
                      <div className="flex items-center text-sm text-[#5a7280]" data-id="element-897">
                        <Users className="mr-1.5 h-4 w-4" data-id="element-898" />
                        {site.affectedGuards} guards
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-899">
                      <div className="flex items-center text-sm text-[#5a7280]" data-id="element-900">
                        <Clock className="mr-1.5 h-4 w-4" data-id="element-901" />
                        {site.overtimeHours}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-902">
                      <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()} data-id="element-903">
                        <button onClick={() => handleViewRoster(site)} className="inline-flex items-center px-3 py-1.5 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-md text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[32px]" data-id="element-904">
                          <Calendar className="mr-1.5 h-3.5 w-3.5" data-id="element-905" />
                          View Roster
                        </button>
                        {site.hasAction ? <button onClick={() => handleViewAction(site)} className="inline-flex items-center px-3 py-1.5 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-md text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[32px]" data-id="element-906">
                            <CheckCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-500" data-id="element-907" />
                            View Action
                          </button> : <button onClick={() => handleTakeAction(site)} className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-[#ff697f] hover:bg-[#e65a6f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff697f] min-h-[32px]" data-id="element-908">
                            Take Action
                            <ExternalLink className="ml-1.5 h-3.5 w-3.5" data-id="element-909" />
                          </button>}
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-910">
                  <td colSpan={5} className="p-0 border-none" data-id="element-911">
                    <EmptyState icon={Building} title="No sites with overtime" description="All sites in this area are within budget" data-id="element-912" />
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View (hidden on desktop) */}
        <div className="md:hidden space-y-4" data-id="element-913">
          {sites.length > 0 ? sites.map(site => <div key={site.id} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 cursor-pointer hover:border-[#5a7280]/30 transition-colors" onClick={() => handleViewRoster(site)} data-id="element-914">
                <div className="flex justify-between items-start" data-id="element-915">
                  <div data-id="element-916">
                    <h3 className="text-lg font-semibold text-[#062133]" data-id="element-917">
                      {site.name}
                    </h3>
                    <div className="mt-1 flex items-center text-sm font-medium text-rose-600" data-id="element-918">
                      <Coins className="mr-1.5 h-4 w-4" data-id="element-919" />
                      {site.projectedCost}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-2 border-t border-b border-[#e5e5e5]" data-id="element-920">
                  <div data-id="element-921">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-922">
                      Affected Guards
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-923">
                      <Users className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-924" />
                      {site.affectedGuards}
                    </div>
                  </div>
                  <div data-id="element-925">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-926">
                      Overtime Hours
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-927">
                      <Clock className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-928" />
                      {site.overtimeHours}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2" onClick={e => e.stopPropagation()} data-id="element-929">
                  <button onClick={() => handleViewRoster(site)} className="flex-1 flex items-center justify-center px-4 py-3 border border-[#e5e5e5] text-sm font-medium rounded-xl text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[48px]" data-id="element-930">
                    <Calendar className="mr-2 h-4 w-4" data-id="element-931" />
                    View Roster
                  </button>
                  <button onClick={() => site.hasAction ? handleViewAction(site) : handleTakeAction(site)} className={`flex-1 flex items-center justify-center px-4 py-3 border text-sm font-medium rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[48px] ${site.hasAction ? 'border-[#e5e5e5] text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:ring-[#062133]' : 'border-transparent text-white bg-[#ff697f] hover:bg-[#e65a6f] focus:ring-[#ff697f]'}`} data-id="element-932">
                    {site.hasAction ? <>
                        <CheckCircle className="mr-2 h-4 w-4 text-emerald-500" data-id="element-933" />
                        View Action
                      </> : <>
                        Take Action
                        <ExternalLink className="ml-2 h-4 w-4" data-id="element-934" />
                      </>}
                  </button>
                </div>
              </div>) : <EmptyState icon={Building} title="No sites with overtime" description="All sites in this area are within budget" data-id="element-935" />}
        </div>
      </div>

      {/* Action Panel (Create) */}
      <ActionPanel isOpen={selectedSite !== null} siteName={selectedSite?.name || ''} onClose={handleClosePanel} onSubmit={handleSubmitAction} data-id="element-936" />

      {/* Action Detail Panel (View) */}
      <ActionDetailPanel isOpen={viewingActionSite !== null} siteName={viewingActionSite?.name || ''} onClose={handleCloseDetailPanel} data-id="element-937" />

      {/* Roster Modal */}
      <RosterModal isOpen={rosterSite !== null} siteName={rosterSite?.name || ''} siteId={rosterSite?.id || ''} onClose={handleCloseRoster} onTakeAction={() => {
      if (rosterSite) {
        handleTakeAction(rosterSite);
      }
    }} data-id="element-938" />
    </>;
}